import { ICONS } from "../constants/icons";
import Sidebar from "../components/Sidebar";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import MobileHeader from "../components/MobileHeader";
import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import mobileAppBridge from "../utils/mobileAppBridge";
import { FaApple, FaAndroid, FaGlobe } from "react-icons/fa";
import { database } from "../constants/firebase";
import { ref, onValue, get, update } from "firebase/database";

const SubscriptionPlan = () => {
    const navigate = useMoveTo();
    const { currentUser, refreshUser } = useUser();
    const [isHost, setIsHost] = useState(false);
    const [monthlyLoading, setMonthlyLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [yearlyLoading, setYearlyLoading] = useState(false);
    const [trialLoading, setTrialLoading] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [subscriptionType, setSubscriptionType] = useState(null);
    const [error, setError] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [platform, setPlatform] = useState('web'); // 'ios', 'android', or 'web'
    const [voucherCode, setVoucherCode] = useState('');
    const [voucherError, setVoucherError] = useState('');
    const [voucherSuccess, setVoucherSuccess] = useState('');
    const [applyingVoucher, setApplyingVoucher] = useState(false);
    const [activeVouchers, setActiveVouchers] = useState([]);
    const [trialEndDate, setTrialEndDate] = useState(null);
    const [remainingDays, setRemainingDays] = useState(null);

    // Detect platform
    useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isInMobileApp = mobileAppBridge.isInMobileApp();

        if (isInMobileApp) {
            // Check if iOS or Android
            if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
                setPlatform('ios');
            } else if (/android/i.test(userAgent)) {
                setPlatform('android');
            }
        } else {
            setPlatform('web');
        }
    }, []);

    useEffect(() => {
        checkSubscriptionStatus();
    }, [currentUser]);

    // Fetch all active vouchers (backend will validate expiration)
    useEffect(() => {
        const vouchersRef = ref(database, 'zylenvouchers');
        const unsubscribe = onValue(vouchersRef, (snapshot) => {
            if (snapshot.exists()) {
                const vouchersData = snapshot.val();
                const vouchersArray = Object.keys(vouchersData).map(code => ({
                    code,
                    ...vouchersData[code]
                }));

                // Only filter by isActive flag - backend will check expiration
                const active = vouchersArray.filter(voucher => voucher.isActive === true);

                setActiveVouchers(active);
            } else {
                setActiveVouchers([]);
            }
        });

        return () => unsubscribe();
    }, []);

    const checkSubscriptionStatus = async () => {
        if (!currentUser) return;

        try {
            const functions = getFunctions();
            const getStatus =
                httpsCallable(functions, "getSubscriptionStatus");

            const result =
                await getStatus({ userId: currentUser.uid });

            const data = result.data;

            setIsHost(data.isHost);
            setSubscriptionStatus(data.status);

            // ✅ trial support
            if (data.status === "trial") {
                setTrialEndDate(data.trialEndDate);
                setRemainingDays(data.remainingDays);
            } else {
                setTrialEndDate(null);
                setRemainingDays(null);
            }

        } catch (err) {
            console.error(
                "Failed to fetch subscription status:",
                err
            );
        }
    };

    const handleStartTrial = async () => {
        if (!currentUser) return;

        setTrialLoading(true);

        try {
            const userRef = ref(database, `Users/${currentUser.uid}`);

            // 🔥 Check if trial already used
            const snap = await get(userRef);

            if (!snap.exists()) {
                throw new Error("User not found");
            }

            const userData = snap.val();

            if (userData?.hasUsedTrial) {
                window.showToast(
                    "Free trial already used",
                    "error"
                );
                setTrialLoading(false);
                return;
            }

            const now = Date.now();
            const trialEnd =
                now + 14 * 24 * 60 * 60 * 1000;

            // ✅ Grant PRO access
            await update(userRef, {
                role: "Host",
                subscriptionStatus: "trial",

                trialStartDate: now,
                trialEndDate: trialEnd,
                hasUsedTrial: true,
            });

            window.showToast(
                "🎉 14 Day Free Trial Started!",
                "success"
            );

            await refreshUser();
            checkSubscriptionStatus();

        } catch (err) {
            console.error(err);
            window.showToast(err.message, "error");
        }

        setTrialLoading(false);
    };


    const handleApplyVoucher = async () => {
        if (!voucherCode.trim()) {
            setVoucherError('Please enter a voucher code');
            return;
        }

        setApplyingVoucher(true);
        setVoucherError('');
        setVoucherSuccess('');

        try {
            const functions = getFunctions();
            const redeemVoucher = httpsCallable(functions, "redeemVoucher");

            const result = await redeemVoucher({
                userId: currentUser.uid,
                voucherCode: voucherCode.trim().toUpperCase(),
            });

            if (result.data.success) {
                setVoucherSuccess(result.data.message || '✅ Voucher applied successfully!');
                window.showToast(result.data.message || 'Voucher applied successfully! You now have lifetime access.', 'success');

                // Refresh user data and subscription status
                await refreshUser();
                await checkSubscriptionStatus();

                // Clear voucher input
                setVoucherCode('');
            }
        } catch (err) {
            console.error("❌ Voucher redemption failed:", err);
            console.log("Full error object:", JSON.stringify(err, null, 2));

            // Extract user-friendly error message from various Firebase error formats
            let errorMessage = "Invalid voucher code. Please try again.";

            // Try different error properties
            if (err.details) {
                // Firebase Functions v2 error details
                errorMessage = err.details;
            } else if (err.message && !err.message.includes("INTERNAL")) {
                // Regular error message
                errorMessage = err.message;
            } else if (err.code === 'functions/internal') {
                // Firebase Functions internal error - use generic message
                errorMessage = "Invalid voucher code. Please check and try again.";
            } else if (typeof err === 'string') {
                // String error
                errorMessage = err;
            }

            // Clean up "Error: " prefix if present
            errorMessage = errorMessage.replace(/^Error:\s*/i, '');

            setVoucherError(errorMessage);
            window.showToast(errorMessage, "error");
        } finally {
            setApplyingVoucher(false);
        }
    };

    const handleSubscribe = async (type) => {
        console.log('');
        console.log('='.repeat(60));
        console.log('💳 "SUBSCRIBE NOW" BUTTON CLICKED');
        console.log('='.repeat(60));

        if (monthlyLoading || yearlyLoading) {
            console.log('⏳ Already loading, ignoring click');
            return;
        }

        // If in mobile app, open native IAP subscription
        const isInMobileApp = mobileAppBridge.isInMobileApp();
        console.log('📱 Is in mobile app?', isInMobileApp);

        if (isInMobileApp) {
            console.log('✅ MOBILE APP DETECTED - Opening native IAP...');
            const opened = mobileAppBridge.openSubscription();
            console.log('📤 openSubscription() returned:', opened);

            if (opened) {
                console.log('✅ Native IAP modal opened successfully!');
                console.log('🚫 Skipping Stripe checkout');
                console.log('='.repeat(60));
                return; // Don't proceed with Stripe checkout
            }
            // If failed to open IAP, fall through to Stripe
            console.warn('⚠️ Failed to open IAP, falling back to Stripe');
        } else {
            console.log('🌐 WEB BROWSER DETECTED - Proceeding with Stripe...');
        }

        // Web payment flow (Stripe)
        console.log('💰 Starting Stripe checkout...');
        if (type === 'Monthly') {
            setMonthlyLoading(true);
        } else if (type === 'Yearly') {
            setYearlyLoading(true);
        }
        setError(null);

        try {
            const functions = getFunctions();
            const createCheckout = httpsCallable(functions, "createCheckoutSession");

            const result = await createCheckout({
                userId: currentUser.uid,
                email: currentUser.email,
                type: type,
                successUrl: window.location.origin + "/#/dashboard",
                cancelUrl: window.location.origin + "/#/subscription-plan",
            });

            // Redirect to Stripe Checkout
            if (result.data.url) {
                console.log('✅ Stripe checkout URL received:', result.data.url);
                console.log('🔄 Redirecting to Stripe...');
                window.location.href = result.data.url;
            } else {
                console.error('❌ No checkout URL received');
            }
        } catch (err) {
            console.error("❌ Purchase failed:", err);
            setError("Failed to start checkout. Please try again.");
        } finally {
            setMonthlyLoading(false);
            setYearlyLoading(false);
            console.log('='.repeat(60));
        }
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm("Are you sure you want to cancel your subscription? You'll still have access to all premium features until the end of your billing period.")) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const functions = getFunctions();
            const cancelSub = httpsCallable(functions, "cancelSubscription");
            const result = await cancelSub({ userId: currentUser.uid });

            if (result.data.success) {
                // Show success message with toast
                const endDate = new Date(result.data.endDate * 1000);
                const formattedDate = endDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });

                window.showToast(
                    `Subscription canceled. You can continue using all premium features until ${formattedDate}.`,
                    "success"
                );

                // Immediately update status to reflect cancellation
                setSubscriptionStatus('canceling');
                setIsHost(true); // Keep Host status until period ends

                await refreshUser();
                await checkSubscriptionStatus();
            }
        } catch (err) {
            console.error("Failed to cancel subscription:", err);
            const errorMessage = err.message || "Failed to cancel subscription. Please try again.";
            window.showToast(errorMessage, "error");
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Check if returning from successful checkout
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        if (sessionId) {
            // Payment successful, refresh user data
            setTimeout(async () => {
                await refreshUser();
                checkSubscriptionStatus();
                // Clean URL
                window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
            }, 1000);
        }
    }, []);

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            {/* Mobile Header */}
            <MobileHeader />

            <div className="flex">
                {/* Sidebar */}
                <Sidebar />

                {/* Main Content */}
                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div>
                        {/* Top Header */}
                        <div className="flex items-center justify-between px-0 md:px-6">
                            <button onClick={() => navigate(-1)} className="flex-shrink-0">
                                <img
                                    src={ICONS.arrowBack}
                                    className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                                    alt="Back"
                                />
                            </button>
                            <div className="flex-1 flex flex-col items-center text-center">
                                <h2 className="text-[34px] md:text-[48px] font-semibold leading-tight">

                                </h2>
                                <h3 className="text-base md:text-xl font-semibold mt-1">
                                    {isHost ? "Manage your subscription" : "Choose your subscription plan"}
                                </h3>
                            </div>
                            <div className="w-4 md:w-5" />
                        </div>

                        {error && (
                            <div className="mt-6 max-w-2xl mx-auto p-4 bg-red-100 dark:bg-red-900/20 border border-red-500 rounded-xl">
                                <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
                            </div>
                        )}


                        {/* Current Status */}
                        {subscriptionStatus === "trial" ? (
                            <div className="mt-8 max-w-2xl mx-auto p-4 bg-blue-100 dark:bg-blue-900/20 border border-blue-500 rounded-xl">
                                <p className="text-blue-700 dark:text-blue-300 text-center font-bold">
                                    🚀 Free Trial Active
                                </p>

                                <p className="text-sm text-blue-600 dark:text-blue-400 text-center mt-2">
                                    {remainingDays} day
                                    {remainingDays !== 1 ? "s" : ""} remaining
                                </p>

                                {trialEndDate && (
                                    <p className="text-xs text-blue-500 text-center mt-1">
                                        Ends on{" "}
                                        {new Date(trialEndDate)
                                            .toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                        ) :
                            isHost && (
                                <>
                                    {subscriptionStatus === 'lifetime' ? (
                                        <div className="mt-8 max-w-2xl mx-auto p-4 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/20 dark:to-blue-900/20 border-2 border-purple-500 dark:border-purple-400 rounded-xl">
                                            <p className="text-purple-700 dark:text-purple-300 text-center font-bold text-lg">
                                                🎉 Lifetime Pro Member Access
                                            </p>
                                            <p className="text-sm text-purple-600 dark:text-purple-400 text-center mt-2">
                                                You have unlimited access to all premium features forever!
                                            </p>
                                            {currentUser?.voucherRedeemed && (
                                                <p className="text-xs text-purple-600 dark:text-purple-400 text-center mt-1">
                                                    Voucher: {currentUser.voucherRedeemed}
                                                </p>
                                            )}
                                        </div>
                                    ) : subscriptionStatus === 'canceling' || subscriptionStatus === 'canceled' ? (
                                        <div className="mt-8 max-w-2xl mx-auto p-4 bg-orange-100 dark:bg-orange-900/20 border border-orange-500 rounded-xl">
                                            <p className="text-orange-700 dark:text-orange-400 text-center font-semibold">
                                                ⚠️ Subscription Canceled
                                            </p>
                                            <p className="text-sm text-orange-600 dark:text-orange-300 text-center mt-2">
                                                You can continue using all premium features until the end of your billing period.
                                            </p>
                                            {currentUser?.subscriptionEndDate && (
                                                <p className="text-xs text-orange-600 dark:text-orange-400 text-center mt-1">
                                                    Access ends: {new Date(currentUser.subscriptionEndDate).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="mt-8 max-w-2xl mx-auto p-4 bg-green-100 dark:bg-green-900/20 border border-green-500 rounded-xl">
                                            <p className="text-green-700 dark:text-green-400 text-center font-semibold">
                                                ✅ You're subscribed to the Pro Member
                                            </p>
                                            {subscriptionStatus && subscriptionStatus !== 'active' && (
                                                <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-2">
                                                    Status: {subscriptionStatus}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                        {/* Single Plan Selector */}
                        <div className="mt-12 max-w-2xl mx-auto">
                            <h2
                                type="button"
                                className="w-full rounded-xl text-center bg-[#F5F9FE] border border-gray-200 dark:border-0 dark:bg-[#2B2F36] px-4 py-3 text-sm md:text-base font-semibold text-primary dark:text-white tracking-wide shadow-sm"
                            >
                                Pro Member ($12.99/month)
                            </h2>
                        </div>

                        {/* Features Card */}
                        <div className="mt-2 max-w-2xl mx-auto rounded-xl bg-[#F5F9FE] border border-gray-200 dark:border-0 dark:bg-[#2B2F36] px-6 md:px-10 py-6 md:py-8 shadow-sm">
                            <h4 className="text-lg md:text-2xl font-semibold mb-3">Enhanced Experience</h4>
                            <div className="grid grid-cols-1 gap-y-3 text-sm md:text-base">
                                <div className="flex">
                                    <span className="font-medium w-32 md:w-36 shrink-0">Username:</span>
                                    <span className="flex-1">Golden Glow</span>
                                </div>
                                <div className="flex">
                                    <span className="font-medium w-32 md:w-36 shrink-0">Community:</span>
                                    <span className="flex-1">Create your own community</span>
                                </div>
                                <div className="flex">
                                    <span className="font-medium w-32 md:w-36 shrink-0">Library:</span>
                                    <span className="flex-1">Publish your digital creations for others to access </span>
                                </div>
                                <div className="flex">
                                    <span className="font-medium w-32 md:w-36 shrink-0">Payouts:</span>
                                    <span className="flex-1">Unlock creator benefits when your digital creations are supported by the community</span>
                                </div>
                                <div className="flex">
                                    <span className="font-medium w-32 md:w-36 shrink-0">Store Purchases:</span>
                                    <span className="flex-1">No service fee</span>
                                </div>
                                <div className="flex">
                                    <span className="font-medium w-32 md:w-36 shrink-0">Ads:</span>
                                    <span className="flex-1">Nope. Zero</span>
                                </div>
                                <div className="flex">
                                    <span className="font-medium w-32 md:w-36 shrink-0">Future Upgrades:</span>
                                    <span className="flex-1">Get automatic access to all upcoming features and improvements as the app grows</span>
                                </div>

                            </div>

                        </div>

                        {/* Voucher Code Section - Only show if not a host */}
                        {!isHost && (
                            <div className="mt-8 max-w-2xl mx-auto">
                                <div className="rounded-xl bg-[#F5F9FE] border border-gray-200 dark:border-0 dark:bg-[#2B2F36] px-6 py-6 shadow-sm">
                                    <h4 className="text-lg font-semibold mb-3">Have a Voucher Code?</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                        Enter your voucher code below to unlock special benefits
                                    </p>

                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <input
                                            type="text"
                                            value={voucherCode}
                                            onChange={(e) => {
                                                setVoucherCode(e.target.value.toUpperCase());
                                                setVoucherError('');
                                                setVoucherSuccess('');
                                            }}
                                            placeholder="Enter voucher code"
                                            className="flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1A1A1A] text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-[#327691] placeholder:text-gray-400"
                                            disabled={applyingVoucher}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleApplyVoucher}
                                            disabled={applyingVoucher || !voucherCode.trim()}
                                            className="px-6 py-3 rounded-lg font-semibold bg-[#327691] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2a6278]"
                                        >
                                            {applyingVoucher ? "Applying..." : "Apply"}
                                        </button>
                                    </div>

                                    {voucherError && (
                                        <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/20 border border-red-500 rounded-lg">
                                            <p className="text-red-600 dark:text-red-400 text-sm">❌ {voucherError}</p>
                                        </div>
                                    )}

                                    {voucherSuccess && (
                                        <div className="mt-3 p-3 bg-green-100 dark:bg-green-900/20 border border-green-500 rounded-lg">
                                            <p className="text-green-700 dark:text-green-400 text-sm">✅ {voucherSuccess}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* CTA Buttons */}
                        <div className="mt-4 max-w-2xl mx-auto space-y-2">
                            {!isHost ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleStartTrial}
                                        disabled={trialLoading}
                                        className="w-full px-4 py-3 rounded-xl font-semibold border border-[#327691] text-black dark:text-white"
                                    >
                                        {trialLoading
                                            ? "Starting Trial..."
                                            : "Start 14-Day Free Trial"}
                                    </button>
                                    <div className="text-center text-sm text-gray-500 dark:text-gray-400 pb-2">
                                        or subscribe with payment
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleSubscribe('Monthly')}
                                        disabled={monthlyLoading}
                                        className="w-full px-4 py-3 rounded-xl font-semibold border border-[#327691] bg-[#327691] text-white dark:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {monthlyLoading ? "Loading..." : "Subscribe Now - $12.99/month"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleSubscribe('Yearly')}
                                        disabled={yearlyLoading}
                                        className="w-full px-4 py-3 rounded-xl font-semibold border border-[#327691] bg-[#327691] text-white dark:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {yearlyLoading ? "Loading..." : "Subscribe Now - $120/year"}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => navigate(ROUTES.DASHBOARD)}
                                        className="w-full px-4 py-3 rounded-xl font-semibold border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#2B2F36] text-black dark:text-white transition-colors"
                                    >
                                        Back to Dashboard
                                    </button>
                                    {subscriptionStatus === 'active' && subscriptionStatus !== 'lifetime' && (
                                        <button
                                            type="button"
                                            onClick={() => platform === 'web' ? handleCancelSubscription() : setShowCancelModal(true)}
                                            disabled={loading}
                                            className="w-full px-4 py-3 rounded-xl font-semibold border border-red-500 bg-transparent text-red-600 dark:text-red-400 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading ? "Processing..." : platform === 'web' ? "Cancel Subscription" : "How to Cancel"}
                                        </button>
                                    )}
                                    {subscriptionStatus === 'lifetime' && (
                                        <div className="text-center text-sm text-gray-600 dark:text-gray-400 py-2">
                                            💎 You have lifetime access - no subscription to cancel!
                                        </div>
                                    )}
                                    {(subscriptionStatus === 'canceling' || subscriptionStatus === 'canceled') && platform === 'web' && (
                                        <div className="text-center text-sm text-gray-600 dark:text-gray-400 py-2">
                                            💡 Your premium features remain active until your billing period ends.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Powered by Stripe */}
                        <div className="mt-6 text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Secure payments powered by {platform === 'web' ? 'Stripe' : platform === 'ios' ? 'Apple' : 'Google Play'}
                            </p>
                        </div>
                    </div>
                </main>
            </div>

            {/* Cancel Subscription Instructions Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white dark:bg-[#2A2A2A] rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
                        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {platform === 'ios' ? (
                                    <FaApple className="text-2xl" />
                                ) : platform === 'android' ? (
                                    <FaAndroid className="text-2xl text-green-500" />
                                ) : (
                                    <FaGlobe className="text-2xl text-blue-500" />
                                )}
                                <h2 className="text-2xl font-bold text-black dark:text-white">
                                    Cancel Subscription
                                </h2>
                            </div>
                            <button
                                onClick={() => setShowCancelModal(false)}
                                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="px-6 py-6 space-y-4">
                            {platform === 'ios' ? (
                                // iOS Instructions
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                        <p className="text-sm text-blue-800 dark:text-blue-300 font-semibold mb-2">
                                            📱 Your subscription is managed through the App Store
                                        </p>
                                        <p className="text-xs text-blue-700 dark:text-blue-400">
                                            To cancel, you'll need to use your iPhone's Settings app.
                                        </p>
                                    </div>

                                    <div className="space-y-3">
                                        <h3 className="font-semibold text-lg">How to Cancel on iPhone:</h3>
                                        <ol className="space-y-3 text-sm">
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                                                <span>Open the <strong>Settings</strong> app on your iPhone</span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                                                <span>Tap your name at the top</span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                                                <span>Tap <strong>Subscriptions</strong></span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">4</span>
                                                <span>Find and tap <strong>Zylen Pro Member</strong></span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">5</span>
                                                <span>Tap <strong>Cancel Subscription</strong></span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">6</span>
                                                <span>Confirm the cancellation</span>
                                            </li>
                                        </ol>
                                    </div>

                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                            💡 <strong>Note:</strong> You'll continue to have access until the end of your current billing period.
                                        </p>
                                    </div>
                                </div>
                            ) : platform === 'android' ? (
                                // Android Instructions
                                <div className="space-y-4">
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                        <p className="text-sm text-green-800 dark:text-green-300 font-semibold mb-2">
                                            📱 Your subscription is managed through Google Play
                                        </p>
                                        <p className="text-xs text-green-700 dark:text-green-400">
                                            To cancel, you'll need to use the Google Play Store app.
                                        </p>
                                    </div>

                                    <div className="space-y-3">
                                        <h3 className="font-semibold text-lg">How to Cancel on Android:</h3>
                                        <ol className="space-y-3 text-sm">
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                                                <span>Open the <strong>Google Play Store</strong> app</span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                                                <span>Tap the <strong>Menu</strong> icon (three lines) at the top left</span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                                                <span>Tap <strong>Subscriptions</strong></span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">4</span>
                                                <span>Find and tap <strong>Zylen Pro Member</strong></span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">5</span>
                                                <span>Tap <strong>Cancel Subscription</strong></span>
                                            </li>
                                            <li className="flex gap-3">
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">6</span>
                                                <span>Follow the on-screen instructions to confirm</span>
                                            </li>
                                        </ol>
                                    </div>

                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                            💡 <strong>Note:</strong> You'll continue to have access until the end of your current billing period.
                                        </p>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4">
                            <button
                                onClick={() => setShowCancelModal(false)}
                                className="w-full px-4 py-3 rounded-xl font-semibold bg-gray-200 dark:bg-gray-700 text-black dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionPlan;
